/**
 * @author jakubbujakowski
 * @since 2020-4-27
 */

import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { LOADING_STATE } from "@typings/reduxThunkActions";

import {
  useFeatureToggle,
  useFeatureToggleManager,
} from "@hooks/useFeatureToggle";
import { useAuth } from "@hooks/useAuth";

import {
  fetchPricings,
  fetchUrssafPricings,
} from "@app/redux/booking/booking.actions";
import {
  getServiceLocationId,
  getSelectedCleaningFrequency,
  getVoucher,
  getSelectedPricePlanUuid,
  getSelectedCleaningProductsIds,
  getNonStandardFrequencyShifts,
  getHasUrssaf,
  getSelectedTasksDuration,
} from "@app/redux/booking/booking.selectors";

const BookingPriceUpdater = React.memo(() => {
  const dispatch = useDispatch();
  const { status: featureToggleStatus } = useFeatureToggleManager();
  const { isUserLoggedIn } = useAuth();

  // FTs
  const cleaningProductsSalesEnabled = useFeatureToggle(
    "cleaning_products_sale",
  );
  const moreOftenFrequencyB2CEnabled = useFeatureToggle(
    "more_often_frequency_for_b2c",
  );

  // Selectors
  const serviceLocationId = useSelector(getServiceLocationId);
  const duration = useSelector(getSelectedTasksDuration);
  const frequency = useSelector(getSelectedCleaningFrequency);
  const voucherToken = useSelector(getVoucher);
  const selectedPricePlanUuid = useSelector(getSelectedPricePlanUuid);
  const cleaningProductsIds = useSelector(getSelectedCleaningProductsIds);
  const nonStandardFrequencyShifts = useSelector(getNonStandardFrequencyShifts);
  const hasUrssaf = useSelector(getHasUrssaf);

  // Stringify to avoid unnecessary re-renders
  const stringifyCleaningProductsIds = JSON.stringify(cleaningProductsIds);
  const stringifyNonStandardFrequencyShifts = JSON.stringify(
    nonStandardFrequencyShifts,
  );

  const featureToggleAreFetched = React.useMemo(
    () =>
      [LOADING_STATE.SUCCEEDED, LOADING_STATE.FAILED].includes(
        featureToggleStatus,
      ),
    [featureToggleStatus],
  );

  React.useEffect(() => {
    if (!featureToggleAreFetched || !serviceLocationId) return;

    const reqFeatureToggles = {
      cleaning_products_sale: cleaningProductsSalesEnabled,
      more_often_frequency_for_b2c: moreOftenFrequencyB2CEnabled,
    };
    const reqOpts = {
      abortEnabled: true,
    };

    dispatch(
      hasUrssaf
        ? fetchUrssafPricings(serviceLocationId, reqFeatureToggles, reqOpts)
        : fetchPricings(serviceLocationId, reqFeatureToggles, reqOpts),
    );
  }, [
    dispatch,
    featureToggleAreFetched,
    serviceLocationId,
    frequency,
    duration,
    voucherToken,
    selectedPricePlanUuid,
    stringifyCleaningProductsIds,
    stringifyNonStandardFrequencyShifts,
    hasUrssaf,
    cleaningProductsSalesEnabled,
    moreOftenFrequencyB2CEnabled,
    isUserLoggedIn,
  ]);

  return null;
});

BookingPriceUpdater.displayName = "BookingPriceUpdater";

export { BookingPriceUpdater };
